<template lang="html">
    <section id="ai-usage">
        <h6>E. AI Usage Declaration</h6>
        <p v-if="aiPriorDeclared" class="ai-prior-summary">Your declared AI usage for previously submitted versions:<br><br><span v-for="(line, index) in aiPriorSummaryLines" v-bind:key="index">{{ line }}<br></span></p>
        <p v-else class="ai-no-prior-usage">No AI usage has been declared for previously submitted versions.</p>

        <label id="aiUsageLabel">
            <template v-if="aiPriorDeclared">Did you or any coauthors use any <strong>additional AI tools</strong> for any <strong>additional purposes</strong> for this revision?</template>
            <template v-else>Did you or any coauthors use generative AI tools for this revision?</template>
            <span class="required"></span>
        </label>
        <div role="radiogroup" aria-labelledby="aiUsageLabel">
            <label><input type="radio" value="no" v-model="aiUsage" v-on:change="onChange()"> No</label><br>
            <label><input type="radio" value="yes" v-model="aiUsage" v-on:change="onChange()"> Yes</label>
        </div>
        <p v-if="aiPriorDeclared && aiUsage === 'yes'" class="ai-info-text">Please select the AI tools used and/or the purpose of use before submitting.</p>
        <p v-if="errors && errors['aiUsage']" class="ai-error-text">{{ errors['aiUsage'][0] }}</p>

        <div v-if="aiUsage === 'yes'" id="aiDetailsSection">
            <div v-for="(group, groupId, index) in aiQuestionGroups" v-bind:key="groupId" class="ai-question-group">
                <label>{{ index + 1 }}. {{ group.title }} (select all that apply)<span v-if="!aiPriorDeclared" class="required"></span></label>
                <div
                    v-for="question in group.questions"
                    v-bind:key="question.question_id"
                    class="ai-question-option"
                    v-bind:class="{ declared: isPreviouslyDeclared(question.question_id) }">
                    <label>
                        <input
                            type="checkbox"
                            v-bind:value="question.question_id"
                            v-model="selectedAIQuestions"
                            v-bind:disabled="isPreviouslyDeclared(question.question_id)"
                            v-on:change="onChange()">
                        {{ question.title }}
                    </label>

                    <div v-if="question.question_type === 2 && selectedAIQuestions.includes(question.question_id)" class="ai-freetext-input">
                        <input
                            type="text"
                            v-model="aiFreetextValues[question.question_id]"
                            v-on:change="onChange()"
                            placeholder="Please specify..."
                            v-bind:aria-label="question.title" />
                    </div>
                </div>
            </div>

            <label>{{ aiAcknowledgmentNumber }}. AI Usage Acknowledgment<span class="required"></span></label>
            <div class="ai-acknowledgment-container">
                <input type="checkbox" id="aiAcknowledgment" v-model="aiAcknowledgment" v-on:change="onChange()">
                <label for="aiAcknowledgment">
                    I confirm that the outputs of all tools used in the preparation of this manuscript were verified by the authors who take full responsibility for the final content of the manuscript. This statement is made in accordance with <a href="https://support.jmir.org/hc/en-us/articles/13387268671771-Do-you-allow-the-use-of-ChatGPT-or-other-generative-language-models-and-how-should-this-be-reported" target="_blank">JMIR Publications Policy on the use of Generative AI</a>.<span class="required"></span>
                </label>
            </div>
            <p v-if="errors && errors['aiAcknowledgment']" class="ai-error-text">{{ errors['aiAcknowledgment'][0] }}</p>
        </div>
    </section>
</template>

<script>
export default {
    props: {
        articleId: {
            type: [Number, String],
            required: true
        },
        editDecisionId: {
            type: [Number, String],
            default: null
        },
        changeEvent: {
            type: [String, Boolean],
            default: false
        }
    },
    data() {
        return {
            aiUsage: null,
            aiQuestionGroups: {},
            selectedAIQuestions: [],
            aiFreetextValues: {},
            aiAcknowledgment: false,
            aiPriorDeclared: null,
            aiPriorSummary: null,
            previouslySelectedQuestionIds: [],
            errors: {}
        };
    },
    computed: {
        // Numbered after the question groups, matching step3.tpl's acknowledgmentNumber.
        aiAcknowledgmentNumber() {
            return Object.keys(this.aiQuestionGroups).length + 1;
        },
        // priorSummary is "\n"-joined, one line per question group (e.g. "Purpose: ..." /
        // "AI Tool: ...") — see ArticleAIUsageController::buildPriorSummary().
        aiPriorSummaryLines() {
            return this.aiPriorSummary ? this.aiPriorSummary.split('\n') : [];
        },
        // Options already declared at original submission or an earlier revision are rendered
        // pre-checked and disabled (see isPreviouslyDeclared()), so they stay visible in
        // selectedAIQuestions for display purposes but must never be re-submitted as this
        // revision's own "additional" answer.
        newSelections() {
            return this.selectedAIQuestions.filter(questionId => !this.previouslySelectedQuestionIds.includes(questionId));
        },
        payload() {
            return {
                editDecisionId: this.editDecisionId,
                aiUsage: this.aiUsage,
                selectedAIQuestions: this.newSelections,
                aiFreetextValues: this.aiFreetextValues,
                aiAcknowledgment: this.aiAcknowledgment
            };
        },
        // Same rules as computeErrors(), without writing to this.errors — lets the parent
        // reactively disable the Submit button before the author ever clicks it, instead of
        // only rejecting the click after the fact.
        isComplete() {
            return Object.keys(this.computeErrors()).length === 0;
        }
    },
    methods: {
        onChange() {
            if (this.changeEvent && window.top[this.changeEvent]) {
                window.top[this.changeEvent]();
            }

            this.$emit('completion-change', this.isComplete);
        },
        isPreviouslyDeclared(questionId) {
            return this.previouslySelectedQuestionIds.includes(questionId);
        },
        async fetchAIUsage() {
            await axios
                .get(`/api/articles/${this.articleId}/ai-usage?editDecisionId=${this.editDecisionId}`)
                .then(response => {
                    let data = response.data;

                    this.aiQuestionGroups = data.questionGroups;
                    this.aiPriorDeclared = data.priorDeclared;
                    this.aiPriorSummary = data.priorSummary;
                    this.previouslySelectedQuestionIds = data.previouslySelectedQuestionIds || [];

                    if (data.currentAnswer) {
                        this.aiUsage = data.currentAnswer.aiUsage;

                        let savedSelections = data.currentAnswer.selectedAIQuestions || [];

                        // Merge in the previously-declared selector questions so they render
                        // checked (and disabled, via isPreviouslyDeclared()) alongside this
                        // revision's own selections, without counting toward aiAcknowledgment
                        // or being resubmitted as new (see newSelections()/payload()).
                        this.selectedAIQuestions = Array.from(new Set([...savedSelections, ...this.previouslySelectedQuestionIds]));
                        this.aiFreetextValues = data.currentAnswer.aiFreetextValues || {};
                        this.aiAcknowledgment = savedSelections.length > 0;
                    }

                    this.$emit('loaded');
                    this.$emit('completion-change', this.isComplete);
                })
                .catch(() => {
                    this.$emit('loaded');
                    this.$emit('completion-change', this.isComplete);
                });
        },
        // Shared by validate() (writes this.errors, used on submit) and isComplete (read-only,
        // used to reactively enable/disable the Submit button) so the two can never drift apart.
        computeErrors() {
            let errors = {};

            if (!this.aiUsage) {
                errors['aiUsage'] = ['Please answer this question before continuing.'];
            } else if (this.aiUsage === 'yes') {
                // Every group needs at least one checked option, and previously declared (greyed)
                // ones count toward that, so an author who reused an already-declared tool for a
                // new purpose isn't forced to pick another tool. At least one option overall must
                // be new, though, since only new selections are recorded for this revision.
                for (let groupId in this.aiQuestionGroups) {
                    let group = this.aiQuestionGroups[groupId];
                    let hasSelection = group.questions.some(question => this.selectedAIQuestions.includes(question.question_id));

                    if (!hasSelection) {
                        errors['aiUsage'] = ['Please select at least one option.'];
                        break;
                    }
                }

                if (!errors['aiUsage'] && this.newSelections.length === 0) {
                    errors['aiUsage'] = ['Please select at least one additional purpose or tool.'];
                }

                for (let questionId of this.selectedAIQuestions) {
                    let question = null;

                    for (let groupId in this.aiQuestionGroups) {
                        question = this.aiQuestionGroups[groupId].questions.find(q => q.question_id === questionId);
                        if (question) break;
                    }

                    if (question && question.question_type === 2 && !this.aiFreetextValues[questionId]) {
                        errors['aiUsage'] = ['Please specify a value for each selected option.'];
                    }
                }

                if (!this.aiAcknowledgment) {
                    errors['aiAcknowledgment'] = ['Please check the acknowledgment statement.'];
                }
            }

            return errors;
        },
        validate() {
            this.errors = this.computeErrors();

            return Object.keys(this.errors).length === 0;
        }
    },
    updated() {
        updateIFrameHeight();
    },
    async created() {
        await this.fetchAIUsage();
    }
};
</script>

<style lang="css" scoped>
#ai-usage input[type="checkbox"],
#ai-usage input[type="radio"] {
    opacity: 1;
    position: static;
    width: auto;
    height: auto;
    margin-right: 6px;
    cursor: pointer;
}
#ai-usage label {
    font-weight: normal;
}
.ai-no-prior-usage {
    font-weight: bold;
}
.required::after {
    content: "*";
    color: red;
}
.ai-error-text {
    color: red;
}
.ai-info-text {
    color: #e09202;
}
.ai-prior-summary {
    line-height: 1.6;
}
/* aiUsageForm.css hides this by default (step3 shows it via JS); here it's already gated by
   v-if, so it must be visible whenever it's rendered. */
.ai-freetext-input {
    display: block;
}
/* Keep the acknowledgment checkbox beside its statement; the label is otherwise a full-width
   inline-block here and wraps onto its own line below the checkbox. */
.ai-acknowledgment-container {
    display: flex;
    align-items: flex-start;
}
.ai-acknowledgment-container label {
    flex: 1;
    margin: 0;
}
#ai-usage .ai-acknowledgment-container input[type="checkbox"] {
    margin-top: 4px;
}
</style>
